import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';

type MarketPoint = {
  category: string;
  current: number;
  future: number;
  currentLabel: string;
  futureLabel: string;
};

const data: MarketPoint[] = [
  {
    category: 'Generative AI',
    current: 1.5,
    future: 10,
    currentLabel: '2024',
    futureLabel: '2030',
  },
  {
    category: 'Total AI Market',
    current: 6.5,
    future: 28,
    currentLabel: '2024',
    futureLabel: '2030',
  },
];

const maxValue = 30;

const formatCurrency = (value: number) => `$${value}B`;

export default function MarketOpportunityChart() {
  const ref = useRef<HTMLDivElement | null>(null);
  const isInView = useInView(ref, { once: true, amount: 0.35 });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, ease: 'easeOut' }}
      className="rounded-lg bg-card/50 p-4 md:p-6"
    >
      <div className="grid gap-6 lg:grid-cols-2">
        {data.map((item) => {
          const currentHeight = `${(item.current / maxValue) * 100}%`;
          const futureHeight = `${(item.future / maxValue) * 100}%`;

          return (
            <div key={item.category} className="rounded-xl border border-border/70 bg-background/40 p-4">
              <div className="mb-4 flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-base font-semibold text-foreground md:text-lg">{item.category}</h3>
                  <p className="text-sm text-muted-foreground">
                    Growth from {formatCurrency(item.current)} to {formatCurrency(item.future)}
                  </p>
                </div>
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <span className="h-3 w-3 rounded-full bg-[hsl(354_82%_30%)]" />
                    {item.currentLabel}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="h-3 w-3 rounded-full bg-primary" />
                    {item.futureLabel}
                  </div>
                </div>
              </div>

              <div className="flex h-[260px] items-end justify-center gap-10 rounded-lg border border-border/60 bg-muted/10 px-6 py-4 md:h-[320px]">
                <div className="flex h-full flex-col justify-end gap-3 text-center">
                  <div className="flex h-full items-end">
                    <div
                      className="w-20 rounded-t-xl bg-[hsl(354_82%_30%)] shadow-sm transition-all duration-700"
                      style={{ height: currentHeight }}
                      aria-label={`${item.currentLabel} value ${formatCurrency(item.current)}`}
                    />
                  </div>
                  <div>
                    <p className="text-lg font-semibold text-foreground">{formatCurrency(item.current)}</p>
                    <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{item.currentLabel}</p>
                  </div>
                </div>

                <div className="flex h-full flex-col justify-end gap-3 text-center">
                  <div className="flex h-full items-end">
                    <div
                      className="w-20 rounded-t-xl bg-primary shadow-sm transition-all duration-700"
                      style={{ height: futureHeight }}
                      aria-label={`${item.futureLabel} value ${formatCurrency(item.future)}`}
                    />
                  </div>
                  <div>
                    <p className="text-lg font-semibold text-foreground">{formatCurrency(item.future)}</p>
                    <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{item.futureLabel}</p>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </motion.div>
  );
}
